import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { TermsDocument } from '@nexus/shared';

import { renderMarkdown } from '../lib/markdown';
import { apiGet } from '../lib/api';
import { Alert, Card, LoadingPage, PageHeader, When } from '../ui';

/**
 * The creator terms, as a page anybody can read.
 *
 * The signup form renders the same text raw (it hashes what it shows); this
 * is the readable copy for the footer, for a creator checking what they
 * agreed to, and for a client asking what creators are bound by. Always the
 * CURRENT published version — the version a particular creator accepted, and
 * its exact text, is on their Profile.
 */
export function Terms(): React.JSX.Element {
  const [terms, setTerms] = useState<TermsDocument | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const body = await apiGet<{ terms: TermsDocument }>('/terms/creator');
        setTerms(body.terms);
      } catch {
        setError('Could not load the creator terms right now. Try again in a moment.');
      }
    })();
  }, []);

  if (error) {
    return (
      <>
        <PageHeader eyebrow="Nexus" title="Creator terms" />
        <Alert tone="critical">{error}</Alert>
      </>
    );
  }
  if (!terms) return <LoadingPage eyebrow="Nexus" title="Creator terms" />;

  return (
    <>
      <PageHeader
        eyebrow="Nexus"
        title="Creator terms"
        lead={
          <>
            Version {terms.version}, published <When value={terms.publishedAt} />. Accepting
            them is part of creating an account; the exact text you accepted is on your Profile.
          </>
        }
      />
      <Card>
        <article className="legal">{renderMarkdown(terms.bodyText)}</article>
      </Card>
      <p className="muted">
        See also the <Link to="/privacy">privacy policy</Link>.
      </p>
    </>
  );
}
